import React, { useState, useEffect, useCallback, useRef } from "react";
import SoftphoneServiceClass from "../../softphoneServices/softphoneService";
import styles from "./Softphone.module.scss";
import getConfig from "../../config/config";
import {
  SoftphoneError,
  ErrorTypes,
  ErrorCodes,
} from "../../softphoneServices/softphoneErrors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faToggleOn,
  faToggleOff,
  faPhone,
  faPhoneSlash,
  faMicrophone,
  faMicrophoneSlash,
  faCog,
  faVolumeUp,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

interface SipCredentials {
  username: string;
  extension: string;
  password: string;
}

const Softphone = ({ config, initialPhoneNumber }: { config: any; initialPhoneNumber?: string }) => {
  // Generate server profiles dynamically from config
  const serverProfiles = {
    cloud: {
      label: config.sipServer || "localhost",
      server: config.sipServer || "localhost",
    },
    local: {
      label: "localhost",
      server: "localhost",
    },
  };

  const [sipCredentials, setSipCredentials] = useState<SipCredentials | null>(null);
  const [credentialsLoading, setCredentialsLoading] = useState(true);
  const [selectedServer, setSelectedServer] = useState("");
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");
  const [serverStatus, setServerStatus] = useState("Disconnected");
  const [showSettings, setShowSettings] = useState(false);
  const [phoneNum, setPhoneNum] = useState("");
  const [callStatus, setCallStatus] = useState("Idle");
  const [autoDialPending, setAutoDialPending] = useState(false);
  const [error, setError] = useState<SoftphoneError | null>(null);
  const [showErrorDropdown, setShowErrorDropdown] = useState(false);

  // Handle initialPhoneNumber - populate and auto-dial after connected
  useEffect(() => {
    if (initialPhoneNumber) {
      console.log("[Softphone] initialPhoneNumber received:", initialPhoneNumber);

      // Check for missing phone number sentinel
      if (initialPhoneNumber === 'PHONE_NUMBER_MISSING') {
        console.log("[Softphone] Phone number not found for tracker");
        setError(new SoftphoneError(
          ErrorTypes.CallError,
          ErrorCodes.PhoneNumberNotFound.code,
          ErrorCodes.PhoneNumberNotFound.message
        ));
        setShowErrorDropdown(true);
        return;
      }

      // Wait for connection before populating and dialing
      if (serverStatus === "Connected") {
        console.log("[Softphone] Already connected, populating and dialing...");
        setPhoneNum(initialPhoneNumber);
        setAutoDialPending(true);
      } else {
        // Store for later - will populate and dial once connected
        console.log("[Softphone] Not connected yet, will auto-dial once connected");
        setPhoneNum(initialPhoneNumber);
        setAutoDialPending(true);
      }
    }
  }, [initialPhoneNumber]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [volume, setVolume] = useState(50); // Default volume level, adjust as needed
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const softphoneService = useRef<SoftphoneServiceClass | null>(null);

  const setupEventHandlers = (service: SoftphoneServiceClass) => {
    service.on('serverConnecting', () => setIsConnecting(true));
    service.on('registered', () => {
      console.log("Registration successful - updating status to Connected");
      setIsConnecting(false);
      setServerStatus("Connected");
    });
    service.on('remoteStreamObtained', handleRemoteStream);
    service.on('incomingCall', () => {
      setCallStatus("Incoming Call");
    });
    service.on('callActive', () => {
      setCallStatus("In Call");
    });
    service.on('callEnded', () => {
      setCallStatus("Ended");
      setTimeout(() => setCallStatus("Idle"), 3500);
    });
    service.on('callFailed', () => {
      setCallStatus("Failed");
      setTimeout(() => setCallStatus("Idle"), 3500);
    });
    service.on('disconnected', () => {
      setServerStatus("Disconnected");
      setCallStatus("Idle");
    });
    service.on('error', (error) => setError(error));
    service.on('connectionFailed', () => {
      console.error("Connection failed.");
      setIsConnecting(false);
      setServerStatus("Disconnected");
      setCallStatus("Idle");
    });
  };

  const initializeSoftphoneService = async () => {
    if (!sipCredentials) return; // Wait for credentials to be fetched

    const newConfig = { ...config };
    const currentProfile = selectedServer || "cloud";
    newConfig.sipServer = serverProfiles[currentProfile]?.server || serverProfiles.cloud.server;
    newConfig.sipUsername = sipCredentials.username;
    newConfig.sipPassword = sipCredentials.password;
    newConfig.sipFullname = sipCredentials.username;

    if (softphoneService.current) {
      softphoneService.current.disconnectUserAgent(); // Clean up the previous instance
    }
    softphoneService.current = new SoftphoneServiceClass(newConfig);

    // Setup event handlers immediately after creating the service
    setupEventHandlers(softphoneService.current);

    // Initialize audio devices after the softphone service is set up
    const devices = await softphoneService.current.getAudioInputDevices();
    setAudioDevices(devices);
    if (devices.length > 0) {
      setSelectedAudioDevice(devices[0].deviceId);
    }
  };

  // Fetch SIP credentials for logged-in user
  useEffect(() => {
    fetch('/api/sip/credentials')
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch SIP credentials');
        }
        return res.json();
      })
      .then((data) => {
        setSipCredentials({
          username: data.username,
          extension: data.extension,
          password: data.password,
        });
        setCredentialsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching SIP credentials:', err);
        setCredentialsLoading(false);
        setError(
          new SoftphoneError(
            ErrorTypes.ConnectionError,
            ErrorCodes.ConnectionFailed.code,
            'Failed to load SIP credentials. Please refresh the page.'
          )
        );
      });
  }, []);

  useEffect(() => {
    initializeSoftphoneService();
  }, [sipCredentials, selectedServer]);

  // Auto-connect to SIP server when softphone opens and credentials are ready
  useEffect(() => {
    const autoConnect = async () => {
      if (
        sipCredentials &&
        softphoneService.current &&
        serverStatus === "Disconnected" &&
        !isConnecting
      ) {
        console.log("[Softphone] Auto-connecting to SIP server...");
        setIsConnecting(true);
        setServerStatus("Connecting");
        try {
          await softphoneService.current.initUserAgent();
          // Status will be updated by the 'registered' event listener
        } catch (error) {
          console.error("[Softphone] Auto-connect failed:", error);
          setServerStatus("Disconnected");
        } finally {
          setIsConnecting(false);
        }
      }
    };

    // Small delay to ensure service is fully initialized
    const timer = setTimeout(autoConnect, 500);
    return () => clearTimeout(timer);
  }, [sipCredentials, softphoneService.current]);

  // Auto-dial when connected and initialPhoneNumber was provided
  useEffect(() => {
    if (
      autoDialPending &&
      serverStatus === "Connected" &&
      phoneNum &&
      callStatus === "Idle" &&
      softphoneService.current
    ) {
      console.log("[Softphone] Auto-dialing:", phoneNum);
      setAutoDialPending(false);
      setCallStatus("Dialing");
      softphoneService.current.makeCall(phoneNum);
    }
  }, [serverStatus, autoDialPending, phoneNum, callStatus]);

  const handleAudioDeviceChange = (event) => {
    setSelectedAudioDevice(event.target.value);
    // Add any additional logic to handle the device change
  };

  const handleServerChange = (event) => {
    const server = event.target.value;
    setSelectedServer(server);
    setServerStatus("Disconnected");
    initializeSoftphoneService();
  };

  // Handle server and call status change
  const updateStatus = useCallback((server, call) => {
    setServerStatus(server);
    setCallStatus(call);
    if (call === "Ended" || call === "Failed") {
      setTimeout(() => setCallStatus("Idle"), 3000);
    }
  }, []);

  // Handle error messages
  const handleError = useCallback((error) => {
    setError(error);
    setShowErrorDropdown(!!error); // Show error dropdown when error occurs
  }, []);

  // Function to close the error dropdown
  const handleCloseError = () => {
    setError(null);
    setShowErrorDropdown(false);
  };

  // Function to toggle mute state
  const handleToggleMute = () => {
    softphoneService.current?.toggleMute();
    setIsMuted(!isMuted); // Toggle the isMuted state to reflect the change
  };

  const handleServerConnection = useCallback(async () => {
    if (serverStatus === "Connected") {
      // If already connected, disconnect
      updateStatus("Disconnecting", callStatus);
      await softphoneService.current?.disconnectUserAgent();
      updateStatus("Disconnected", "Idle");
    } else {
      // If not connected, try to connect
      if (isConnecting) return; // Prevent multiple connections at once
      setIsConnecting(true);
      updateStatus("Connecting", callStatus);
      try {
        await softphoneService.current?.initUserAgent();
        // Status will be updated by the 'registered' event listener
        // Don't set to "Connected" here - wait for actual registration
      } catch (error) {
        console.error("Failed to connect to server:", error);
        updateStatus("Disconnected", callStatus);
      } finally {
        setIsConnecting(false);
      }
    }
  }, [serverStatus, callStatus, isConnecting, updateStatus]);

  const handleCallOrAnswer = useCallback(async () => {
    switch (callStatus) {
      case "Incoming Call":
        console.log("Answering incoming call...");
        updateStatus(serverStatus, "In Call");
        await softphoneService.current?.answerCall();
        break;
      default:
        if (serverStatus === "Connected" && phoneNum) {
          console.log("Making a new call...");
          updateStatus(serverStatus, "Dialing");
          await softphoneService.current?.makeCall(phoneNum);
        }
        break;
    }
  }, [phoneNum, serverStatus, callStatus, updateStatus]);

  const handleEndOrReject = useCallback(() => {
    if (["Dialing", "In Call", "Incoming Call"].includes(callStatus)) {
      updateStatus(serverStatus, "Ended");
      softphoneService.current?.endCall();
    } else {
      console.log(
        `No action taken. Call status '${callStatus}' does not require ending or rejecting.`
      );
    }
  }, [callStatus, serverStatus, updateStatus]);

  // Handle remote stream
  const handleRemoteStream = useCallback((stream) => {
    console.log(
      `[${new Date().toISOString()}] Set audio element with remote stream and remote track:`,
      stream,
      stream.getTracks()
    );
    setRemoteStream(stream); // This will trigger the useEffect to run
  }, []);

  useEffect(() => {
    // If remoteStream changes and audioRef is defined, set the stream as the source
    if (audioRef.current && remoteStream) {
      audioRef.current.srcObject = remoteStream;
      // You might need to call play() if autoPlay isn't working as expected
      audioRef.current.play().catch((e) => {
        console.error("Error playing the audio stream:", e);
      });
    }
  }, [remoteStream]);

  const handleVolumeChange = (event) => {
    const newVolume = parseInt(event.target.value, 10);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100; // HTML audio element expects a value between 0 and 1
    }
  };

  // Handle cleanup on unmount
  useEffect(() => {
    console.log("Component mounted");
    return () => {
      console.log("Component unmounted - cleaning up");
      // Disconnect when component actually unmounts
      if (softphoneService.current) {
        softphoneService.current.endCall();
        softphoneService.current.disconnectUserAgent();
      }
    };
  }, []);

  const toggleSettingsPopup = () => {
    setShowSettings(!showSettings);
  };

  return (
    <div className={styles.softphone}>
      {error && (
        <div
          className={`${styles.errorPopup} ${
            showErrorDropdown ? styles.visible : ""
          }`}
        >
          {error.userMessage}
          <button onClick={handleCloseError}>Close</button>
        </div>
      )}
      <div className={styles.statusContainer}>
        <div
          className={`${styles.serverStatusIndicator} ${
            styles[serverStatus.toLowerCase()]
          }`}
        >
          <div className={styles.userProfile}>
            <div className={styles.statusInfo}>
              <div className={styles.statusRow}>
                <span
                  className={`${styles.connectionIndicator} ${
                    serverStatus === "Connected"
                      ? styles.connected
                      : serverStatus === "Connecting"
                      ? styles.connecting
                      : styles.disconnected
                  }`}
                ></span>
                <div>
                  {credentialsLoading ? (
                    <b>Loading credentials...</b>
                  ) : sipCredentials ? (
                    <b>{sipCredentials.username}</b>
                  ) : (
                    <b>Credential error</b>
                  )}
                  {sipCredentials && <span> (ext. {sipCredentials.extension})</span>}
                </div>
              </div>
              <div className={styles.buttonRow}>
                <button
                  onClick={handleServerConnection}
                  className={`${styles.connectButton} ${
                    styles[serverStatus.toLowerCase()]
                  }`}
                  title={serverStatus === "Connected" ? "Disconnect" : "Connect"}
                  disabled={!sipCredentials}
                >
                  <FontAwesomeIcon
                    icon={
                      serverStatus === "Connected" ? faToggleOn : faToggleOff
                    }
                    size="lg"
                  />
                </button>
                <button
                  onClick={toggleSettingsPopup}
                  className={`${styles.settingsButton} ${styles.settingsTooltip}`}
                  title={showSettings ? "Back to Dialpad" : "Settings"}
                >
                  <FontAwesomeIcon icon={showSettings ? faArrowLeft : faCog} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!showSettings && (
        <div className={styles.volumeControl}>
          <FontAwesomeIcon className={styles.volumeIcon} icon={faVolumeUp} />
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={handleVolumeChange}
          />
          <div className={styles.buttonWrapper}>
            <button
              onClick={handleToggleMute}
              className={`${styles.muteButton} ${isMuted ? styles.active : ""}`}
            >
              <FontAwesomeIcon
                icon={isMuted ? faMicrophoneSlash : faMicrophone}
              />
            </button>
            <div className={`${styles.tooltip} ${styles.muteTooltip}`}>
              {isMuted ? "Unmute" : "Mute"}
            </div>
          </div>
        </div>
      )}
      <audio ref={audioRef} autoPlay hidden />

      {!showSettings ? (
        <>
          <div className={styles.separator}></div>
          <div
            className={`${styles.callStatusIndicator} ${
              styles[callStatus.toLowerCase().replace(/\s+/g, "-")]
            }`}
          >
            Call Status: {callStatus.charAt(0).toUpperCase() + callStatus.slice(1)}
          </div>
          <div className={styles.inputGroup}>
            <input
              type="text"
              className={styles.inputField}
              value={phoneNum}
              onChange={(e) => setPhoneNum(e.target.value)}
              placeholder="Enter Phone Number"
            />
          </div>
          <div className={styles.dialpad}>
            {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map(
              (key) => (
                <button
                  key={key}
                  onClick={() => setPhoneNum(phoneNum + key)}
                  className={styles.dialButton}
                >
                  {key}
                </button>
              )
            )}
          </div>
          <div className={styles.buttonWrapper}>
            <button
              onClick={handleCallOrAnswer}
              className={`${styles.callButton} ${
                ["Idle", "Incoming Call"].includes(callStatus)
                  ? ""
                  : styles.disabled
              }`}
              disabled={!["Idle", "Incoming Call"].includes(callStatus)}
            >
              <FontAwesomeIcon icon={faPhone} />
              {/* <div className={`${styles.tooltip} ${styles.callTooltip}`}>
                {callStatus === "Incoming Call" ? "Answer" : "Call"}
              </div> */}
            </button>

            <button
              onClick={handleEndOrReject}
              className={`${styles.rejectButton} ${
                ["Dialing", "In Call", "Incoming Call"].includes(callStatus)
                  ? ""
                  : styles.disabled
              }`}
              disabled={
                !["Dialing", "In Call", "Incoming Call"].includes(callStatus)
              }
            >
              <FontAwesomeIcon icon={faPhoneSlash} />
              {/* <div className={`${styles.tooltip} ${styles.rejectTooltip}`}>
                End/Reject Call
              </div> */}
            </button>
          </div>
        </>
      ) : (
        <div className={styles.settingsContainer}>
          <div className={styles.settingsHeader}>
            <h3>Settings</h3>
          </div>
          <div className={styles.serverSelectionContainer}>
            <div className={styles.buttonWrapper}>
              <button
                onClick={handleServerConnection}
                className={`${styles.connectButton} ${
                  styles[serverStatus.toLowerCase()]
                }`}
              >
                <FontAwesomeIcon
                  icon={
                    serverStatus === "Connected" ? faToggleOn : faToggleOff
                  }
                  size="lg"
                />
                <div
                  className={`${styles.tooltip} ${styles.connectTooltip}`}
                >
                  {serverStatus === "Connected" ? "Disconnect" : "Connect"}
                </div>
              </button>
            </div>
            <label htmlFor="server-select">Select Server:</label>
            <select
              id="server-select"
              value={selectedServer}
              onChange={handleServerChange}
            >
              {Object.entries(serverProfiles).map(([key, profile]) => (
                <option key={key} value={key}>
                  {profile.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.audioSelectionContainer}>
            <label htmlFor="audio-select">Select Input Device:</label>
            <select
              id="audio-select"
              value={selectedAudioDevice}
              onChange={handleAudioDeviceChange}
            >
              {audioDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId}`}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default Softphone;
